/**
 * `LevelBar` — barra dei Livelli `e-0..e-5` che indica **il Livello posseduto**
 * su una competenza e se soddisfa il Livello richiesto da una professione.
 *
 * - le celle non sono mai riempite né colorate: restano neutre e servono solo da
 *   scala di riferimento;
 * - un marker (cursore triangolare) e il riquadro del bordo puntano **sempre al
 *   Livello posseduto** (incluso `e-0` = "Nessuno");
 * - marker e riquadro sono **verdi** se il Livello posseduto soddisfa il
 *   requisito (o non c'è requisito), **gialli** se è nel range supportato ma
 *   sotto la soglia richiesta, **rossi** se è `e-0` o sotto il minimo Livello
 *   supportato dalla competenza (Livello non utile).
 *
 * Il primo quadratino è sempre `e-0` ("Nessuno") e i Livelli `e-1..e-5` che la
 * competenza non può avere (assenti da `available`) sono mostrati **disattivati**
 * (scala di riferimento non applicabile), senza mai essere il bersaglio del
 * marker se non coincidono col Livello posseduto.
 *
 * Estratto da `JobDetail` per essere riusato dalla guida "Analisi Gap"
 * (`GapAnalysisGuide`), così la legenda mostra esattamente la stessa resa.
 */
import { LIVELLO_LABELS, livelloRank, type Livello } from '../shared/dto';

export interface LevelBarProps {
  /** Livello posseduto (o `null` = nessuno, reso come `e-0`). */
  owned: Livello | null;
  /** Livello richiesto dalla professione (o `null` = nessun requisito). */
  required: Livello | null;
  /** Livelli che la competenza può assumere (scala di riferimento applicabile). */
  available: Livello[];
}

export function LevelBar({ owned, required, available }: LevelBarProps) {
  const rawOwnedRank = owned === null ? 0 : livelloRank(owned); // 0..5
  const requiredRank = required === null ? null : livelloRank(required); // 1..5
  // Rank dei Livelli disponibili (1..5) per la competenza; gli altri sono spenti.
  const availableRanks = new Set(available.map((l) => livelloRank(l)));
  const maxAvailableRank =
    availableRanks.size > 0 ? Math.max(...availableRanks) : null;
  const minAvailableRank =
    availableRanks.size > 0 ? Math.min(...availableRanks) : null;
  // Cap al massimo Livello supportato dalla competenza: se il posseduto lo
  // supera, il marker/riquadro puntano al massimo previsto (non a un Livello
  // disattivato oltre la scala della competenza).
  const ownedRank =
    maxAvailableRank !== null && rawOwnedRank > maxAvailableRank
      ? maxAvailableRank
      : rawOwnedRank;

  // Il Livello posseduto è sotto il minimo Livello previsto dalla competenza
  // (ma comunque > e-0)? È trattato come "nessun Livello utile" ⇒ ROSSO.
  const belowMinSupported =
    minAvailableRank !== null && ownedRank > 0 && ownedRank < minAvailableRank;

  // Stato del Livello (cappato al massimo supportato), che determina il
  // colore di marker/riquadro/cella:
  //  - 'none'  → ROSSO: e-0 oppure sotto il minimo Livello supportato dalla
  //              competenza (Livello non utile);
  //  - 'meets' → VERDE: raggiunge il Livello richiesto (o non c'è requisito);
  //  - 'gap'   → GIALLO: nel range supportato ma sotto il Livello richiesto.
  const status: 'none' | 'meets' | 'gap' =
    ownedRank === 0 || belowMinSupported
      ? 'none'
      : requiredRank === null || ownedRank >= requiredRank
        ? 'meets'
        : 'gap';
  const meets = status === 'meets';

  const ownedLabel = owned === null ? 'Nessuno' : LIVELLO_LABELS[owned];
  const baseLabel =
    requiredRank === null
      ? `Il tuo livello: ${ownedLabel}, nessun requisito`
      : `Il tuo livello: ${ownedLabel}, richiesto ${LIVELLO_LABELS[required as Livello]}${meets ? ' — soddisfatto' : ' — non raggiunto'}`;
  // Se la competenza non gestisce tutti e cinque i Livelli, esplicita quali sono
  // applicabili (gli altri sono mostrati disattivati).
  const disabledSuffix =
    availableRanks.size > 0 && availableRanks.size < 5
      ? `. Livelli applicabili: ${available.map((l) => LIVELLO_LABELS[l]).join(', ')}`
      : '';
  const ariaLabel = `${baseLabel}${disabledSuffix}`;

  return (
    <span
      className={`level-bar level-bar--${status}`}
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {[0, 1, 2, 3, 4, 5].map((rank) => {
        // e-0 è sempre disattivato; gli altri Livelli sono disattivati quando la
        // competenza non li prevede. Le celle non vengono mai colorate.
        const isDisabled = rank === 0 || !availableRanks.has(rank);
        // Marker + riquadro puntano sempre al Livello posseduto (verde/rosso).
        const isOwned = rank === ownedRank;
        return (
          <span key={rank} className="level-bar__cell-wrap">
            {isOwned && <span className="level-bar__marker" aria-hidden="true" />}
            <span
              className={[
                'level-bar__cell',
                isDisabled ? 'level-bar__cell--disabled' : '',
                isOwned ? 'level-bar__cell--owned' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              e-{rank}
            </span>
          </span>
        );
      })}
    </span>
  );
}

export default LevelBar;
